import type { FC, ReactNode } from 'react';

import { useTheme } from 'styled-components';

import useLockScroll from '@polyai/common/hooks/useLockScroll';
import BackButton from '@polyai/ui/components/atoms/BackButton';
import type { SpacingProps } from '@polyai/ui/components/atoms/Spacing';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';

import EmptyBox from 'assets/svg/illustrations/empty_box.svg';
import EmptyBoxDark from 'assets/svg/illustrations/empty_box_dark.svg';
import NoConnection from 'assets/svg/illustrations/no_connection.svg';
import NoConnectionDark from 'assets/svg/illustrations/no_connection_dark.svg';
import NotFound from 'assets/svg/illustrations/not_found.svg';
import ErrorFallback from 'assets/svg/illustrations/server_error.svg';

export enum EmptyStatus {
  NO_CONNECTION,
  EMPTY,
  ERROR,
  NOT_FOUND,
}

const imageMap: { [key in EmptyStatus]: any } = {
  [EmptyStatus.NO_CONNECTION]: NoConnection,
  [EmptyStatus.EMPTY]: EmptyBox,
  [EmptyStatus.ERROR]: ErrorFallback,
  [EmptyStatus.NOT_FOUND]: NotFound,
};

const imageMapDark: { [key in EmptyStatus]: any } = {
  [EmptyStatus.NO_CONNECTION]: NoConnectionDark,
  [EmptyStatus.EMPTY]: EmptyBoxDark,
  [EmptyStatus.ERROR]: ErrorFallback,
  [EmptyStatus.NOT_FOUND]: NotFound,
};

export type EmptyStateProps = SpacingProps & {
  className?: string;
  type: EmptyStatus;
  title?: string;
  text?: string;
  content?: ReactNode;
  lockScroll?: boolean;
  backPath?: string;
};

const EmptyState: FC<EmptyStateProps> = ({
  type,
  title,
  text,
  content,
  lockScroll = true,
  backPath,
  ...restProps
}) => {
  const theme = useTheme() as any;
  const isDark = theme?.activeColorScheme?.type === 'dark';
  const Image = (isDark ? imageMapDark : imageMap)[type];

  useLockScroll(lockScroll);

  return (
    <>
      {backPath && (
        <Stack align="flex-start" direction="row" width="100%">
          <BackButton to={backPath} />
        </Stack>
      )}
      <Stack
        align="center"
        direction="column"
        className="my-md2 mx-sm3"
        spacing="sm1"
        {...restProps}
      >
        {<Image />}
        <Stack
          align="center"
          direction="column"
          className="mt-sm1"
          spacing="xs1"
        >
          {title && (
            <Text as="h3" variant="headerRegular">
              {title}
            </Text>
          )}
          {!!text && (
            <Text color="textSecondary" variant="bodySmall">
              {text}
            </Text>
          )}
        </Stack>
        {!!content && content}
      </Stack>
    </>
  );
};

export default EmptyState;
